import { Image, Line, Svg, Text, View } from "@react-pdf/renderer";
import { CellTextBox, safeString, styles } from "./reportingHelpers";
import { formatAttendance } from "./utils";

export const FixtureReportsHeader = ({
  awayTeam,
  fixture,
  homeTeam,
  intl,
  periodsScores,
}) => {
  const startDateOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  const startTimeOptions = { hour: "numeric", minute: "numeric" };
  const competitionName = safeString(fixture, "competitionName");
  const seasonName = safeString(fixture, "seasonName");
  const startDateFormatted = Intl.DateTimeFormat(
    undefined,
    startDateOptions
  ).format(new Date(fixture.startTimeLocal));
  const startTimeFormatted = Intl.DateTimeFormat(
    undefined,
    startTimeOptions
  ).format(new Date(fixture.startTimeLocal));
  const homeTeamName = homeTeam.name || homeTeam.team.name;
  const awayTeamName = awayTeam.name || awayTeam.team.name;

  return (
    <View>
      <View style={styles.header}>
        <View
          style={{ flex: 1, flexDirection: "row", alignItems: "flex-start" }}
        >
          {fixture?.competitionLogo && (
            <Image
              src={fixture.competitionLogo}
              style={{
                maxHeight: 35,
                objectFit: "contain",
              }}
            />
          )}
        </View>
        <View style={{ flex: 1, alignItems: "flex-end" }}>
          <Text style={{ fontWeight: "bold", fontSize: 8 }}>
            {`${competitionName} - ${seasonName}`}
          </Text>
          <Text style={{ fontSize: 8 }}>
            {`${fixture.venueName}`} -{" "}
            {intl.formatMessage({
              id: `reports.header.attendance}`,
              defaultMessage: "Attendance",
            })}
            : {formatAttendance(fixture.attendance)}
          </Text>
          <Text style={{ fontSize: 8 }}>
            {`${startDateFormatted} - ${intl.formatMessage({
              id: "reports.header.starttime",
              defaultMessage: "Start time:",
            })} ${startTimeFormatted}`}
          </Text>
        </View>
      </View>
      <Svg
        height={2}
        width={600}
        style={{ marginLeft: -20, paddingTop: 10, paddingBottom: 10 }}
      >
        <Line x1={0} y1={1} x2={600} y2={1} strokeWidth={1} stroke="grey"/>
      </Svg>
      <View style={styles.header}>
        <View style={{ alignItems: "center", flex: 1 }}>
          <View
            style={{
              marginTop: "3mm",
              alignItems: "center",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                alignContent: "center",
                fontWeight: "bold",
              }}
            >
              <Text
                style={{ fontSize: 10, textAlign: "right", paddingRight: 3 }}
              >
                {homeTeamName}
              </Text>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {homeTeam?.team?.logo && (
                  <Image
                    src={homeTeam.team.logo}
                    style={{ maxHeight: 24, marginLeft: 2, marginRight: 2 }}
                  />
                )}
                <Text style={{ fontSize: 16 }}>{homeTeam.score ?? 0}</Text>
                <Text style={{ fontSize: 12, marginLeft: 2, marginRight: 2 }}>
                  {"-"}
                </Text>
                <Text style={{ fontSize: 16 }}>{awayTeam.score ?? 0}</Text>
                {awayTeam?.team?.logo && (
                  <Image
                    src={awayTeam.team.logo}
                    style={{ maxHeight: 24, marginLeft: 2, marginRight: 2 }}
                  />
                )}
              </View>
              <Text style={{ fontSize: 10, textAlign: "left", paddingLeft: 3 }}>
                {awayTeamName}
              </Text>
            </View>
            <Text style={{ fontSize: 8 }}>
              {"(" + periodsScores.join(", ") + ")"}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export const ScoringTable = ({
  homeTeam,
  awayTeam,
  homeTeamPeriods,
  awayTeamPeriods,
  fixtureProfile,
  fixture,
  intl,
}) => {

  const periodLengthTitle = fixtureProfile.usePeriodLengthCustomized
    ? intl.formatMessage({
      id: "reports.header.scoringByPeriodSplits",
      defaultMessage: "Scoring by period splits",
    })
    : intl.formatMessage({
        id: "reports.header.scoringby",
        defaultMessage: "Scoring by 5 minute intervals",
      },
      { intervalLength: fixtureProfile.periodLength / 2.0 }
    );

  return (
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        marginTop: "10pt",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          height: "100%",
          justifyContent: "center",
          position: "absolute",
          left: 0,
          width: "200px",
        }}
      >
        <Text style={{ fontSize: "7pt" }}>
          {periodLengthTitle}
        </Text>
      </View>
      <View style={{ flexDirection: "row" }}>
        <View
          style={{
            alignItems: "flex-end",
            marginRight: "6pt",
            paddingTop: "7pt",
            justifyContent: "space-around",
            fontFamily: ["Noto Sans", "Noto Sans TC"],
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text style={{ fontSize: "7pt" }}>{homeTeam.name}</Text>
            {homeTeam?.logo && (
              <Image
                src={homeTeam.logo}
                style={{ maxHeight: 12, marginLeft: 4 }}
              />
            )}
          </View>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text style={{ fontSize: "7pt" }}>{awayTeam.name}</Text>
            {awayTeam?.logo && (
              <Image
                src={awayTeam.logo}
                style={{ maxHeight: 12, marginLeft: 4 }}
              />
            )}
          </View>
        </View>
        {homeTeamPeriods.map((period, index) => {
          const hasSecondInterval = period.interval2 !== undefined;
          return (
            <View
              style={{ alignItems: "center", marginRight: "6pt" }}
              key={`scoring-table-periods-${index}`}
            >
              <Text style={{ fontSize: "7pt" }}>{period.label}</Text>
              <View style={{ flexDirection: "row" }}>
                <CellTextBox
                  style={{
                    width: "6mm",
                    fontFamily: ["Noto Sans", "Noto Sans TC"],
                    borderRightWidth: hasSecondInterval ? 0 : 1,
                  }}
                >
                  {period.interval1}
                </CellTextBox>
                {hasSecondInterval && (
                  <CellTextBox
                    style={{
                      width: "6mm",
                      fontFamily: ["Noto Sans", "Noto Sans TC"],
                      borderRightWidth: 1,
                    }}
                  >
                    {period.interval2}
                  </CellTextBox>
                )}
              </View>
              <View style={{ flexDirection: "row" }}>
                <CellTextBox
                  style={{
                    width: "6mm",
                    fontFamily: ["Noto Sans", "Noto Sans TC"],
                    borderRightWidth: hasSecondInterval ? 0 : 1,
                    borderBottomWidth: 1,
                  }}
                >
                  {awayTeamPeriods[index].interval1}
                </CellTextBox>
                {hasSecondInterval && (
                  <CellTextBox
                    style={{
                      width: "6mm",
                      fontFamily: ["Noto Sans", "Noto Sans TC"],
                      borderBottomWidth: 1,
                      borderRightWidth: 1,
                    }}
                  >
                    {awayTeamPeriods[index].interval2}
                  </CellTextBox>
                )}
              </View>
            </View>
          );
        })}
      </View>
      <View style={{
        height: "100%",
        position: "absolute",
        right: 0,
        width: "35px",
      }}>
        {fixture?.seasonLogo &&
          <Image src={fixture.seasonLogo}/>
        }
      </View>
    </View>
  );
};

export const parseScoringIntervals = (teamData, scoringIntervals, intl) => {
  const periods = [];
  const team = teamData.team || teamData;

  const getPeriodLabel = (periodId) => {
    if (periodId < 10) {
      const label = intl.formatMessage({
        id: "reports.header.periodlabel",
        defaultMessage: "Q",
      });
      return `${label}${periodId}`;
    } else {
      const label = intl.formatMessage({
        id: "reports.header.overtimelabel",
        defaultMessage: "OT",
      });
      const overtimePeriodNumber = parseInt(periodId) - 10;
      return `${label}${overtimePeriodNumber}`;
    }
  };

  let newPeriod = {
    interval1: 0,
    interval2: undefined,
    label: "",
    isOverTime: false,
  };

  scoringIntervals.forEach((element, index) => {
    if (element.interval_number < 9) {
      if (element.interval_number % 2) {
        newPeriod.interval1 = element[team.entityId];
        newPeriod.interval2 = undefined;
        newPeriod.label = getPeriodLabel(element.periodId);
        newPeriod.isOverTime = false;
        newPeriod.periodId = element.periodId;

        if (index === scoringIntervals.length - 1) {
          periods.push({ ...newPeriod });
        }
      } else {
        newPeriod.interval2 = element[team.entityId];
        periods.push({ ...newPeriod });
      }
    } else {
      newPeriod.interval1 = element[team.entityId];
      newPeriod.label = getPeriodLabel(element.periodId);
      newPeriod.interval2 = undefined;
      newPeriod.isOverTime = true;
      newPeriod.periodId = element.periodId;
      periods.push({ ...newPeriod });
    }
  });

  return periods;
};
